'use client';

import { Button } from '@/components/button';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: VoidFunction;
}

const ErrorFallback = ({ error, resetErrorBoundary }: ErrorFallbackProps) => {
  const handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  return (
    <div className='flex flex-1 items-center justify-center p-6 bg-surface-primary'>
      <div className='max-w-lg w-full'>
        <div className='bg-surface-secondary rounded-lg p-6 border border-border-primary'>
          <div className='text-center'>
            <div className='text-4xl mb-4'>⚠️</div>
            <h1 className='text-xl font-semibold text-text-primary mb-2'>Oops! Something went wrong</h1>
            <p className='text-text-secondary mb-6'>
              We&apos;ve encountered an unexpected error. Our team has been notified and is working on a fix.
            </p>
            <div className='flex flex-col sm:flex-row gap-3'>
              <Button onClick={resetErrorBoundary} variant='primary' className='flex-1'>
                Try again
              </Button>
              <Button onClick={handleReload} variant='secondary' className='flex-1'>
                Reload page
              </Button>
            </div>
            {/* Show error details in development */}
            {process.env.NEXT_PUBLIC_NODE_ENV === 'development' && error && (
              <details className='mt-6 text-left'>
                <summary className='cursor-pointer text-sm text-text-secondary hover:text-text-primary'>
                  🔍 Error Details (Development)
                </summary>
                <div className='mt-3 p-3 bg-surface-tertiary border border-red-500/20 rounded text-xs font-mono text-text-primary overflow-y-auto max-h-60'>
                  <div className='mb-2'>
                    <strong>Error:</strong> {error.message}
                  </div>
                  {error.stack && (
                    <div>
                      <strong>Stack:</strong>
                      <pre className='whitespace-pre-wrap'>{error.stack}</pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
